import React, { useEffect, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { ArrowDownIcon, ArrowUpIcon, InformationCircleIcon } from '@heroicons/react/16/solid';
import Utilities from '@util/Utilities';
import { usePricingRights } from '@util/PricingRightsHook';
import Tag from '@app/component/tag/Tag';
import Tabs from '@app/component/tabs/Tabs';
import FormButton from '@ui/button/FormButton';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import { PriceRecommendationState } from '@app/model/market_state/PriceRecommendationState';
import PriceRecommendationStateDriverType from '@app/model/market_state/PriceRecommendationStateDriverType';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import { useMarketStateTools } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import {
	RecommendationLimitLines,
	RecommendationLimitTag,
	useRecommendationLimit,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/recommmendation/RecommendationLimitExplanation';
import {
	DRIVER_IMPACT_LEVEL_LABELS,
	getDriverImpactLevel,
	orderRecommendationDrivers,
	recommendationTableRooms,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/recommmendation/overlay/PriceRecommendationDrawerContents.utilities';
import { findValidRecommendationStates, findDateRowRecommendation } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/MarketStateSnapshotDateBasedTableRowFragment.utilities';
import {
	INFO_CARD_TAB_ALL,
	INFO_CARD_TOOLTIP_ID,
	InfoCardScope,
	infoCardAnchor,
	infoCardRoomCount,
	parseInfoCardAnchor,
	percentChange,
	selectInfoCardStates,
	selectInfoCardTabStates,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/RecommendationInfoCard.utilities';

// The information card of the Rec Price cell (a Screen Part). The "i" after the price opens it on a click;
// a click outside, Esc, or a second click closes it. It is information only: the driver bars, the Limit
// Explanation when the recommendation is limited, and the rooms with the rates that go to the booking
// engine. The data is the month data the table already holds, so the card sends no request. One card is
// mounted for the whole table; the "i" carries the date and the scope in its anchor.

const DRIVER_ORDER = [
	PriceRecommendationStateDriverType.COMPETITORS,
	PriceRecommendationStateDriverType.MARKET,
	PriceRecommendationStateDriverType.OCCUPANCY,
];
const DRIVER_LABELS: Record<PriceRecommendationStateDriverType, string> = {
	[PriceRecommendationStateDriverType.COMPETITORS]: 'Competitors',
	[PriceRecommendationStateDriverType.MARKET]: 'Market',
	[PriceRecommendationStateDriverType.OCCUPANCY]: 'Occupancy',
};
const DRIVER_SEGMENT_COLORS = ['bg-blue-300', 'bg-blue-400', 'bg-blue-500', 'bg-blue-600'];

// FormButton forwards no title and no data attributes: the span carries the hover text and the tooltip anchor,
// and the click on the button bubbles to it.
export const RecommendationInfoButton: React.FunctionComponent<{ statDate: string, scope: InfoCardScope }> = ({ statDate, scope }) =>
	<span title="Recommendation details"
				data-tooltip-id={INFO_CARD_TOOLTIP_ID}
				data-tooltip-content={infoCardAnchor(statDate, scope)}
				className="relative inline-flex">
		<FormButton state="text"
								className="relative inline-flex h-3 w-3 items-center justify-center rounded-full p-0 text-gray-400 shadow-none hover:text-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600">
			<span className="sr-only">Recommendation details</span>
			<InformationCircleIcon className="h-3 w-3" aria-hidden="true" />
		</FormButton>
	</span>;

// A narrow screen (below the sm breakpoint, 640 px) has no room at the right of the cell: the card opens below the i.
const useNarrowScreen = (): boolean => {
	const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches);
	useEffect(() => {
		const query = window.matchMedia('(max-width: 639px)');
		const listen = (event: MediaQueryListEvent) => setNarrow(event.matches);
		query.addEventListener('change', listen);
		return () => query.removeEventListener('change', listen);
	}, []);
	return narrow;
};

// Keep narrow cards on the vertical axis instead of flipping beside the icon.
// Constrain the content to the remaining viewport; rows near the bottom use the space above.
const mobileCardPosition = {
	name: 'mobile-card-position',
	fn: ({ rects, elements }: {
		rects: { reference: { x: number, y: number, width: number, height: number }, floating: { width: number, height: number } },
		elements: { floating: HTMLElement },
	}) => {
		const below = rects.reference.y + rects.reference.height + 8;
		const spaceBelow = window.innerHeight - below - 8;
		const useBelow = spaceBelow >= 160;
		const available = Math.max(0, useBelow ? spaceBelow : rects.reference.y - 16);
		const content = elements.floating.querySelector<HTMLElement>('[role="dialog"]');
		if (content) content.style.maxHeight = `${available}px`;
		return {
			x: Math.max(8, Math.min(rects.reference.x + rects.reference.width / 2 - rects.floating.width / 2,
				window.innerWidth - rects.floating.width - 8)),
			y: useBelow ? below : Math.max(8, rects.reference.y - Math.min(rects.floating.height, available) - 8),
		};
	},
};

// Mounted once, next to the table. The fixed strategy keeps the card out of the scrolling table box.
export const RecommendationInfoCardTooltip: React.FunctionComponent = () => {
	const narrow = useNarrowScreen();
	return <Tooltip id={INFO_CARD_TOOLTIP_ID}
						openOnClick
                        closeEvents={{ click: true }}
						clickable
						place={narrow ? 'bottom' : 'right'}
						positionStrategy="fixed"
                        middlewares={narrow ? [mobileCardPosition] : undefined}
                        noArrow={narrow}
						globalCloseEvents={{ escape: true, clickOutsideAnchor: true }}
						className="z-50 !rounded-lg !bg-white !p-0 !opacity-100 shadow-xl ring-1 ring-black ring-opacity-5"
						render={({ content }) => {
							const anchor = parseInfoCardAnchor(content);
							return anchor !== undefined ? <RecommendationInfoCardContent key={content} {...anchor} /> : null;
						}} />;
};

const RecommendationInfoCardContent: React.FunctionComponent<{ statDate: string, scope: InfoCardScope }> = ({ statDate, scope }) => {
	const { companyHotel } = useSelectedCompanyHotel();
	const { canViewRecommendations } = usePricingRights(companyHotel);
	const tools = useMarketStateTools();
	// A date with two or more room types gets one tab per type (Direction C, picked 2026-09-18) and an "All" tab.
	// The default tab is the cheapest room type with a recommendation: the type the cell shows.
	const [pickedTab, setPickedTab] = useState<string | undefined>(undefined);
	const day = tools.findDailySnapshotState(statDate);
	if (day === undefined) return null;
	const states = selectInfoCardStates(findValidRecommendationStates(day, companyHotel, canViewRecommendations), scope);
	if (states.length === 0) return null;
	const submittedIds = new Set(day.marketStateDailySnapshot?.submittedRecommendationStateIds ?? []);
	const allSubmitted = states.every(state => submittedIds.has(state.id));
	const roomCount = infoCardRoomCount(states);
	const typeNames = states.map(state => companyHotel.findRoomMappingTypeName(state.roomMappingType));
	const multi = states.length > 1;
	const defaultTab = findDateRowRecommendation(day, states)?.recommendation.roomMappingType ?? states[0].roomMappingType;
	const tab = pickedTab === INFO_CARD_TAB_ALL || states.some(state => state.roomMappingType === pickedTab) ? pickedTab! : defaultTab;
	const shown = multi ? selectInfoCardTabStates(states, tab) : states;
	const tabOptions = [
		...states.map(state => ({ label: companyHotel.findRoomMappingTypeName(state.roomMappingType), value: state.roomMappingType })),
		{ label: 'All', value: INFO_CARD_TAB_ALL },
	];

	return <div role="dialog" aria-label="Recommendation details" className="w-80 max-w-[calc(100vw-16px)] max-h-[80vh] overflow-auto rounded-lg bg-white px-3 py-3 text-left text-xs text-gray-600 sm:w-96 sm:px-4">
		<div className="flex items-center justify-between gap-x-2">
			<p className="text-sm font-semibold text-gray-900">{Utilities.formatDate(day.calendarDay.date, 'ddd DD/MM')}</p>
			{allSubmitted && <Tag state="info">Submitted</Tag>}
		</div>
		<p className="mt-0.5 text-xs text-gray-500">{typeNames.join(' · ')} · {roomCount} {roomCount === 1 ? 'room' : 'rooms'}</p>
		{multi && <div className="mt-2">
			<Tabs<string> options={tabOptions} value={tab} onChange={value => setPickedTab(value)} />
		</div>}
		<div className={Utilities.classNames('divide-y divide-gray-200', multi ? 'mt-1' : 'mt-2 border-t border-gray-200')}>
			{shown.map(state => <RecommendationInfoSection key={state.id}
																												state={state}
																												companyHotel={companyHotel}
																												showTitle={shown.length > 1}
																												submitted={!allSubmitted && submittedIds.has(state.id)} />)}
		</div>
	</div>;
};

const RecommendationInfoSection: React.FunctionComponent<{
	state: PriceRecommendationState,
	companyHotel: CompanyHotel,
	showTitle: boolean,
	submitted: boolean,
}> = ({ state, companyHotel, showTitle, submitted }) => {
	const limitView = useRecommendationLimit(state);
	const drivers = orderRecommendationDrivers(state.drivers ?? [], DRIVER_ORDER);
	const rooms = recommendationTableRooms(state.minimumBasePriceRoomStates ?? []);
	const currency = companyHotel.settings.main.currency;

	return <div className="py-2">
		{(showTitle || submitted) && <p className="mb-1 flex items-center gap-x-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
			{showTitle && <span>{companyHotel.findRoomMappingTypeName(state.roomMappingType)} · {rooms.length} {rooms.length === 1 ? 'room' : 'rooms'}</span>}
			{submitted && <Tag state="info">Submitted</Tag>}
		</p>}
		{drivers.length > 0 && <dl className="space-y-1 py-1">
			{drivers.map(driver => {
				const level = getDriverImpactLevel(driver.impact);
				return <div key={driver.type} className="flex items-center gap-x-2.5">
					<dt className="w-20 font-medium text-gray-700 sm:w-24">{DRIVER_LABELS[driver.type]}</dt>
					<dd className="flex items-center gap-x-2.5">
						<span className="flex items-center gap-x-1">
							{DRIVER_SEGMENT_COLORS.map((color, index) =>
								<span key={color} className={Utilities.classNames('h-1.5 w-5 rounded-full', index < level ? color : 'bg-gray-200')} />)}
						</span>
						<span className={Utilities.classNames('whitespace-nowrap', level === 0 ? 'italic text-gray-400' : 'text-gray-500')}>{DRIVER_IMPACT_LEVEL_LABELS[level]}</span>
					</dd>
				</div>;
			})}
		</dl>}
		{limitView !== undefined && limitView.limited && <div className="mt-1 flex items-start gap-x-2 border-t border-gray-200 pt-2">
			<RecommendationLimitTag view={limitView} extraClassName="mt-0.5 shrink-0" />
			<div className="min-w-0"><RecommendationLimitLines view={limitView} /></div>
		</div>}
		{rooms.length > 0 && <table className="mt-2 w-full border-t border-gray-200">
			<thead>
			<tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
				<th scope="col" className="py-1 pr-2 font-semibold">Room</th>
				<th scope="col" className="w-px whitespace-nowrap py-1 px-1.5 text-right font-semibold">Base</th>
				<th scope="col" className="w-px whitespace-nowrap py-1 px-1.5 text-right font-semibold">Proposed</th>
				<th scope="col" className="w-px whitespace-nowrap py-1 pl-1.5 text-right font-semibold">Change</th>
			</tr>
			</thead>
			<tbody className="divide-y divide-gray-100">
			{rooms.map(room => {
				const change = percentChange(room.basePrice, room.finalPrice);
				return <tr key={room.roomId} className={room.isAnchor ? 'bg-sky-50' : undefined}>
					<td className="py-1 pr-2 font-medium text-gray-700">
						{room.roomName}
						{room.isAnchor && <span className="block text-xs font-light italic text-gray-400">Base price</span>}
					</td>
					<td className="whitespace-nowrap py-1 px-1.5 text-right tabular-nums text-gray-500">{Utilities.formatAmount(room.basePrice, currency, 0)}</td>
					<td className="whitespace-nowrap py-1 px-1.5 text-right font-semibold tabular-nums text-gray-900">{Utilities.formatAmount(room.finalPrice, currency, 0)}</td>
					<td className="whitespace-nowrap py-1 pl-1.5 text-right tabular-nums">
						{change !== 0 && <span className={Utilities.classNames('inline-flex items-center gap-x-0.5', change > 0 ? 'text-green-500' : 'text-red-500')}>
							{change > 0 ? <ArrowUpIcon className="h-2.5 w-2.5" aria-hidden="true" /> : <ArrowDownIcon className="h-2.5 w-2.5" aria-hidden="true" />}
							{change > 0 ? '+' : ''}{change}%
						</span>}
						{change === 0 && <span className="text-gray-400">0%</span>}
					</td>
				</tr>;
			})}
			</tbody>
		</table>}
	</div>;
};
